"use client";

import { c as _c } from "react/compiler-runtime";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import type { DropPartMarkdownProps } from "@/components/drops/view/part/DropPartMarkdown";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useId, useState } from "react";
import type { ParsedQuorumProposalMarkdown, ParsedQuorumProposalSection } from "./quorumProposalMarkdown";
type CompactMarkdownProps = Pick<DropPartMarkdownProps, "mentionedUsers" | "mentionedGroups" | "mentionedWaves" | "referencedNfts" | "nftLinks" | "onQuoteClick" | "currentDropId" | "hideLinkPreviews" | "quotePath" | "linkPreviewToggleControl" | "onLinkCardActionsActiveChange">;
interface QuorumProposalCompactContentProps extends CompactMarkdownProps {
  readonly proposal: ParsedQuorumProposalMarkdown;
}
function stopPropagation(event: {
  stopPropagation: () => void;
}): void {
  event.stopPropagation();
}
function ProposalMarkdownBlock(t0) {
  const $ = _c(3);
  const {
    markdown,
    markdownProps
  } = t0;
  let t1;
  if ($[0] !== markdown || $[1] !== markdownProps) {
    t1 = <DropPartMarkdownWithPropLogger {...markdownProps} partContent={markdown} />;
    $[0] = markdown;
    $[1] = markdownProps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function ProposalSectionCard(t0) {
  const $ = _c(16);
  const {
    section,
    markdownProps
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => setIsOpen(event.currentTarget.open);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== section.heading) {
    t2 = <span className="tw-text-sm tw-font-semibold tw-text-iron-100">{section.heading}</span>;
    $[1] = section.heading;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = `tw-size-5 tw-flex-shrink-0 tw-text-iron-400 tw-transition-transform ${isOpen ? "tw-rotate-90" : ""}`;
  let t4;
  if ($[3] !== t3) {
    t4 = <ChevronRightIcon className={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <summary onClick={stopPropagation} onKeyDown={stopPropagation} className="tw-flex tw-cursor-pointer tw-list-none tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 [&::-webkit-details-marker]:tw-hidden">{t2}{t4}</summary>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isOpen || $[9] !== markdownProps || $[10] !== section.markdown) {
    t6 = isOpen && <div role="presentation" onClick={stopPropagation} onKeyDown={stopPropagation} className="tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-4 tw-pb-4 tw-pt-3"><ProposalMarkdownBlock markdown={section.markdown} markdownProps={markdownProps} /></div>;
    $[8] = isOpen;
    $[9] = markdownProps;
    $[10] = section.markdown;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isOpen || $[13] !== t5 || $[14] !== t6) {
    t7 = <details open={isOpen} onToggle={t1} className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/70">{t5}{t6}</details>;
    $[12] = isOpen;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
export default function QuorumProposalCompactContent(t0) {
  const $ = _c(27);
  let markdownProps;
  let proposal;
  if ($[0] !== t0) {
    ({
      proposal,
      ...markdownProps
    } = t0);
    $[0] = t0;
    $[1] = markdownProps;
    $[2] = proposal;
  } else {
    markdownProps = $[1];
    proposal = $[2];
  }
  const [areDetailsVisible, setAreDetailsVisible] = useState(false);
  const detailsContainerId = useId();
  const sectionCount = proposal.sections.length;
  const detailsToggleLabel = areDetailsVisible ? "Hide details" : `Show details (${sectionCount})`;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-primary-300">Proposal</p>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== proposal.title) {
    t2 = <h2 className="tw-mb-3 tw-text-lg tw-font-semibold tw-leading-tight tw-text-iron-50">{proposal.title}</h2>;
    $[4] = proposal.title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-iron-400">Summary</p>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== markdownProps || $[8] !== proposal.summaryMarkdown) {
    t4 = <div className="tw-rounded-xl tw-bg-iron-950/80 tw-px-4 tw-py-3">{t3}<ProposalMarkdownBlock markdown={proposal.summaryMarkdown} markdownProps={markdownProps} /></div>;
    $[7] = markdownProps;
    $[8] = proposal.summaryMarkdown;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== areDetailsVisible || $[11] !== detailsContainerId || $[12] !== detailsToggleLabel || $[13] !== sectionCount) {
    t5 = sectionCount > 0 && <div className="tw-mt-3 tw-flex tw-justify-start"><button type="button" aria-expanded={areDetailsVisible} aria-controls={detailsContainerId} onClick={event => {
        stopPropagation(event);
        setAreDetailsVisible(_temp);
      }} onKeyDown={stopPropagation} className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/60 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-iron-200 tw-transition-colors hover:tw-border-iron-700 hover:tw-text-iron-50"><span>{detailsToggleLabel}</span><ChevronRightIcon className={`tw-size-4 tw-flex-shrink-0 tw-text-iron-400 tw-transition-transform ${areDetailsVisible ? "tw-rotate-90" : ""}`} /></button></div>;
    $[10] = areDetailsVisible;
    $[11] = detailsContainerId;
    $[12] = detailsToggleLabel;
    $[13] = sectionCount;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t2 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-primary-400/20 tw-bg-primary-500/5 tw-px-4 tw-py-4">{t1}{t2}{t4}{t5}</div>;
    $[15] = t2;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== areDetailsVisible || $[20] !== detailsContainerId || $[21] !== markdownProps || $[22] !== proposal.sections) {
    t7 = areDetailsVisible && <div id={detailsContainerId} className="tw-flex tw-flex-col tw-gap-y-2">{proposal.sections.map(section => <ProposalSectionCard key={section.heading} section={section} markdownProps={markdownProps} />)}</div>;
    $[19] = areDetailsVisible;
    $[20] = detailsContainerId;
    $[21] = markdownProps;
    $[22] = proposal.sections;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t6 || $[25] !== t7) {
    t8 = <div className="tw-flex tw-flex-col tw-gap-y-3">{t6}{t7}</div>;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function _temp(previousValue) {
  return !previousValue;
}